import { useEffect, useState, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

export type MerchantRole = 'super_admin' | 'manager' | 'view_only' | null;

interface ProfessionalMerchantState {
  role: MerchantRole;
  loading: boolean;
  merchantId: string | null;
  isSuperAdmin: boolean;
  isManager: boolean;
  isViewOnly: boolean;
  canManagePayments: boolean;
  canManageRefunds: boolean;
  canManageProducts: boolean;
  canViewFees: boolean;
  canAccessSettings: boolean;
  canManageTeam: boolean;
}

export function useProfessionalMerchant(): ProfessionalMerchantState {
  const { user, loading: authLoading } = useAuth();
  const [role, setRole] = useState<MerchantRole>(null);
  const [loading, setLoading] = useState(true);
  const [merchantId, setMerchantId] = useState<string | null>(null);
  const lastUserId = useRef<string | null>(null);

  useEffect(() => {
    if (authLoading) {
      setLoading(true);
      return;
    }

    if (!user) {
      setRole(null);
      setMerchantId(null);
      setLoading(false);
      lastUserId.current = null;
      return;
    }

    if (lastUserId.current === user.id) {
      return;
    }

    const fetchRole = async () => {
      setLoading(true);
      lastUserId.current = user.id;

      try {
        // Only fetch professional merchant records (standalone + .professional. domain)
        const { data: merchantUsers, error } = await supabase
          .from('merchant_users')
          .select('merchant_id, role, merchants!inner(platform_type, shopify_store_domain)')
          .eq('user_id', user.id);

        if (error || !merchantUsers || merchantUsers.length === 0) {
          setRole(null);
          setMerchantId(null);
        } else {
          // Filter for professional merchants only
          const profRecord = merchantUsers.find((mu: any) => {
            const m = mu.merchants;
            return m?.platform_type === 'standalone' && m?.shopify_store_domain?.includes('.professional.');
          });

          // Check stored preference as fallback
          const storedMerchantId = localStorage.getItem('active_merchant_id');
          const storedRecord = merchantUsers.find((mu: any) => mu.merchant_id === storedMerchantId);
          
          // Prefer stored if it's a professional record, otherwise use detected professional
          let activeRecord = profRecord;
          if (storedRecord) {
            const storedMerchant = (storedRecord as any).merchants;
            if (storedMerchant?.platform_type === 'standalone' && storedMerchant?.shopify_store_domain?.includes('.professional.')) {
              activeRecord = storedRecord;
            }
          }

          if (activeRecord) {
            setRole(activeRecord.role as MerchantRole);
            setMerchantId(activeRecord.merchant_id);
            localStorage.setItem('active_merchant_id', activeRecord.merchant_id);
          } else {
            setRole(null);
            setMerchantId(null);
          }
        }
      } catch (error) {
        console.error('Error fetching professional merchant role:', error);
        setRole(null);
        setMerchantId(null);
      } finally {
        setLoading(false);
      }
    };

    fetchRole();
  }, [user, authLoading]);

  const isSuperAdmin = role === 'super_admin';
  const isManager = role === 'manager';
  const isViewOnly = role === 'view_only';

  return {
    role,
    loading,
    merchantId,
    isSuperAdmin,
    isManager,
    isViewOnly,
    canManagePayments: isSuperAdmin || isManager,
    canManageRefunds: isSuperAdmin || isManager,
    canManageProducts: isSuperAdmin || isManager,
    canViewFees: isSuperAdmin,
    canAccessSettings: isSuperAdmin,
    canManageTeam: isSuperAdmin,
  };
}
